import { ACCOUNT_FEATURE, APP_FEATURE, bem, selectAccountId, selectAccountName } from '@socialive/common';
import { useFeature } from '@socialive/features';
import {
  Button,
  DateInputField,
  Form,
  Modal,
  SelectField,
  SwitchField,
  TextInputField,
  TimeInputField,
} from '@socialive/ui';
import { add, format, isFuture, isWithinInterval, parse, sub } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getStudioURL } from '~/helpers/broadcastHelpers';
import { scheduleBroadcast, selectAccountChannelsSorted, selectApiBaseUrl, selectIsProduction } from '~/store';
import trackMixPanelEvent from '../../helpers/trackMixPanelEvent';
import './createBroadcastModal.css';

const FORM = {
  CHANNEL: 'channel',
  TITLE: 'title',
  DATE: 'date',
  TIME: 'time',
  SCHEDULE: 'schedule',
};

const MODE = {
  BROADCAST: '1',
  RECORDING: '2',
};

const CreateBroadcastModal = ({ modal, reAuth, trackEvent, loadBroadcasts }) => {
  const dispatch = useDispatch();

  const [channelMode, setChannelMode] = useState(MODE.BROADCAST);
  const [channel, setChannel] = useState(parseInt(sessionStorage.getItem('createBroadcastChannel'), 10));

  const accountName = useSelector(selectAccountName);
  const accountId = useSelector(selectAccountId);
  const apiBaseUrl = useSelector(selectApiBaseUrl);
  const isProduction = useSelector(selectIsProduction);
  const channels = useSelector(selectAccountChannelsSorted);
  const channelOptions = useMemo(() => channels.map(({ id, name }) => ({ label: name, value: id })), [channels]);

  const [, { enabled: isRolesFeatureEnabled }] = useFeature(ACCOUNT_FEATURE.USER_ROLES);
  const [, { enabled: scheduleBroadcastEnable }] = useFeature(ACCOUNT_FEATURE.SCHEDULE_BROADCAST);
  const [, featureAppConfig] = useFeature(APP_FEATURE.APP_CONFIG);

  const form = useForm({
    defaultValues: {
      [FORM.CHANNEL]: channel,
      [FORM.TITLE]: `${accountName} Live on ${format(Date.now(), "MMM d 'at' h:mma")}`,
      [FORM.DATE]: format(new Date(), 'yyyy-MM-dd'),
      [FORM.TIME]: format(add(new Date(), { hours: 1 }), 'HH:00'),
      [FORM.SCHEDULE]: false,
    },
  });
  const { control, getValues } = form;
  const channelSelection = useWatch({ control, name: FORM.CHANNEL });
  const setSchedule = useWatch({ control, name: FORM.SCHEDULE });

  useEffect(() => {
    if (channelSelection !== channel) {
      sessionStorage.setItem('createBroadcastChannel', channelSelection);
      localStorage.setItem('createBroadcastChannel', channelSelection);

      const currentAccountSettingsString = localStorage.getItem('accountSettings');
      const currentAccountSettings = JSON.parse(currentAccountSettingsString) ?? {};
      const updateAccountChannel = { ...currentAccountSettings[accountId], currentChannel: channelSelection };
      const updatedAccountSettings = { ...currentAccountSettings, [accountId]: updateAccountChannel };
      localStorage.setItem('accountSettings', JSON.stringify({ ...updatedAccountSettings }));
      setChannel(channelSelection);
    }
  }, [accountId, channel, channelSelection]);

  const onSubmit = useCallback(
    async ({ channel: broadcastChannel, title, date, time, schedule }) => {
      const studioURL = getStudioURL(sessionStorage.getItem('studioURLOverride'), true);
      const windowParams =
        sessionStorage.getItem('studioWindowType') === 'tab'
          ? ''
          : `width=${window.screen.availWidth},height=${window.screen.availHeight},titlebar=no,menubar=no,status=no`;

      window.webcasterWindow = window.open('', 'webcaster', windowParams);
      const webcaster = window.webcasterWindow;
      const params = {
        method: 'post',
        headers: {
          authorization: sessionStorage.getItem('authToken'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: title,
          channelId: broadcastChannel,
        }),
      };

      let broadcastHash;
      if (broadcastChannel) {
        try {
          const response = await fetch(`${apiBaseUrl}/broadcast/create`, params);
          if (response.status === 401) {
            trackEvent('401', { source: 'createBroadcast' });
            // eslint-disable-next-line no-console
            console.error('Received 401 response from createBroadcast (CreateBroadcast). Redirecting to login.');
            webcaster.close();
            reAuth('dashboard');
            return;
          }

          broadcastHash = await response.text();

          const version = featureAppConfig?.versions?.studio ?? Date.now();
          const URL = isProduction
            ? `${studioURL}/?hash=${broadcastHash}&mode=${channelMode}&v=${version}`
            : `${studioURL}/?hash=${broadcastHash}&host=${apiBaseUrl}&mode=${channelMode}&v=${version}`;
          webcaster.location.replace(URL);

          loadBroadcasts();
          trackMixPanelEvent('Create new broadcast', { 'Broadcast Type': channelMode });
          trackEvent('Broadcast Created', { broadcast_hash: broadcastHash });
        } catch (error) {
          console.error('create broadcast API call failed. ', error);
        }

        if (broadcastHash && schedule && date && time) {
          try {
            const scheduledStart = parse(`${date} ${time}`, 'yyyy-MM-dd HH:mm', new Date()).valueOf() / 1000;
            await dispatch(scheduleBroadcast({ scheduledStart, broadcastHash })).unwrap();
          } catch {
            toast.error('Failed to set broadcast date');
          }
        }
        modal.close({ trigger: 'CreateBroadcastModal@onSubmit' });
      } else {
        // eslint-disable-next-line no-console
        console.error('createBroadcastChannel was null and caused the broadcast creation to be aborted.');
      }
    },
    [
      apiBaseUrl,
      channelMode,
      dispatch,
      featureAppConfig?.versions?.studio,
      isProduction,
      loadBroadcasts,
      modal,
      reAuth,
      trackEvent,
    ]
  );

  return (
    <Modal title="New Broadcast" visible onCancel={modal.close} width={520} onCloseButtonClick={modal.close}>
      <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-5 m-0.5" noValidate>
        <SelectField name={FORM.CHANNEL} label="Choose a Socialive channel" options={channelOptions} />
        <TextInputField
          name={FORM.TITLE}
          label="Title of your broadcast"
          maxLength={{ value: 128, message: 'Name is too long' }}
          required="Required"
        />
        {scheduleBroadcastEnable && (
          <>
            <SwitchField
              orientation="horizontal"
              name={FORM.SCHEDULE}
              label="Schedule Broadcast"
              toolTipText="Set a planned broadcast start time"
            />
            <div className={bem`createBroadcastModal__schedular--${{ setSchedule }} flex gap-6`}>
              <div className="grow basis-0">
                <DateInputField
                  name={FORM.DATE}
                  label="Broadcast Date"
                  deps={[FORM.TIME]}
                  minDate={Date.now()}
                  maxDate={add(Date.now(), { years: 10 })}
                  validate={(date) =>
                    !getValues(FORM.SCHEDULE) ||
                    !date ||
                    isWithinInterval(new Date(date), {
                      start: sub(Date.now(), { days: 1 }),
                      end: add(Date.now(), { years: 10 }),
                    }) ||
                    'Date must be in the future'
                  }
                />
              </div>
              <div className="grow basis-0">
                <TimeInputField
                  name={FORM.TIME}
                  label="Broadcast Time"
                  deps={[FORM.TIME]}
                  validate={(time) =>
                    !getValues(FORM.SCHEDULE) ||
                    !time ||
                    isFuture(parse(getValues(FORM.DATE), 'yyyy-MM-dd', new Date())) ||
                    time > format(Date.now(), 'HH:mm') ||
                    'Start time must be in future'
                  }
                />
              </div>
            </div>
          </>
        )}
        <div className="flex justify-between">
          {!isRolesFeatureEnabled && (
            <div className="modal-broadcast-options">
              <button
                type="submit"
                onClick={() => setChannelMode(MODE.BROADCAST)}
                className="modal-option-box left"
                id="broadcastButton"
              >
                <img className="option-icon" src="/live.png" alt="live stream" />
                <div className="option-box-header">Broadcast live</div>
                <div className="option-box-body">
                  <span className="option-box-copy">Broadcast live video to your social and digital channels</span>
                </div>
              </button>
              <button
                type="submit"
                onClick={() => setChannelMode(MODE.RECORDING)}
                className="modal-option-box right"
                id="recordButton"
              >
                <img className="option-icon" src="/record.png" alt="record only" />
                <div className="option-box-header">Record a video</div>
                <div className="option-box-body">
                  <span className="option-box-copy">
                    Record a video to your Socialive account or privately to your computer
                  </span>
                </div>
              </button>
            </div>
          )}
          {isRolesFeatureEnabled && (
            <>
              <Button context="subtle" onClick={modal.close}>
                Cancel
              </Button>
              <Button context="primary" type="submit">
                Done
              </Button>
            </>
          )}
        </div>
      </Form>
    </Modal>
  );
};

CreateBroadcastModal.propTypes = {
  modal: PropTypes.shape({
    visible: PropTypes.bool,
    open: PropTypes.func,
    update: PropTypes.func,
    close: PropTypes.func,
  }).isRequired,
  loadBroadcasts: PropTypes.func.isRequired,
  reAuth: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

export default CreateBroadcastModal;
